import { Flex, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"

import Icon from "../../../../../Icon"
import ReceiptCard, { SAMPLE } from "../ReceiptCard/ReceiptCard"

import styles from "./livePreview.module.less"

const { Text } = Typography

// Live preview rail: a light blue gradient panel pinned to the right of the
// Configure wizard. It shows the "before" (a bare, easy-to-dispute statement
// line) and the "after" (the rich digital receipt) and updates in real time as
// the merchant types. The region tied to the current step is highlighted so
// the merchant sees exactly what each field changes. Hidden on the Review step
// (the main pane already shows the finished receipt there).
const LivePreview = ({ buildingBlocks, section }) => {
  const { t } = useTranslation()
  const bb = buildingBlocks || {}

  // The statement line a bank shows today, derived from the business name ALONE
  // (§11.12): uppercased, non-alphanumerics stripped, truncated, then wrapped
  // in the sample acquirer prefix/suffix. The merchant's real billing
  // descriptors are deliberately not used here — the spec states that the
  // billing descriptor and every other field leave the Before card untouched,
  // so only Business name updates it as the merchant types.
  const cryptic = (bb.business_info?.business_name || "WEBSTORE")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 9)
  const beforeDescriptor = `SQ *${cryptic} 855-700`

  return (
    <aside className={styles.preview} data-testid="deflect-configure-live-preview">
      <div className={styles.inner}>
        <div className={styles.head}>
          <Text className={styles.eyebrow}>{t("Live preview")}</Text>
          <Text type="secondary">{t("What cardholders see in their banking app.")}</Text>
        </div>

        {/* Before: a bare statement line that is easy to dispute. */}
        <div className={styles.stage}>
          <Text className={styles.stageLabel}>{t("Before")}</Text>
          {/* No step highlights the Before card: it reacts to Business name
              only, which lives on step 1 where the After card is the thing
              that changes. */}
          <div className={styles.beforeCard}>
            <Flex justify="space-between" align="flex-start" gap={12}>
              <Flex gap={12} align="center">
                <div className={styles.beforeIcon}>
                  <Icon type="bank" style={{ fontSize: 16 }} />
                </div>
                <div className={styles.beforeText}>
                  <Text className={styles.beforeDesc}>{beforeDescriptor}</Text>
                  <Text type="secondary" className={styles.beforeMeta}>
                    {SAMPLE.paidOn} · {t("Card")} •••• {SAMPLE.card}
                  </Text>
                </div>
              </Flex>
              <Text className={styles.beforeAmount}>{SAMPLE.amount}</Text>
            </Flex>
            <div className={styles.beforeNote}>
              <Icon type="circle_info" style={{ fontSize: 16 }} />
              <span>{t("I don't recognize this charge")}</span>
            </div>
          </div>
        </div>

        <div className={styles.connector}>
          <Icon type="chevron_down_small" />
        </div>

        {/* After: the rich digital receipt (frosted-glass variant), with the
            active step highlighted. */}
        <div className={styles.stage}>
          <Text className={styles.stageLabel}>{t("After")}</Text>
          <ReceiptCard buildingBlocks={bb} highlight={section} preview />
        </div>
      </div>
    </aside>
  )
}

const mapStateToProps = (state) => ({
  buildingBlocks: state.receiptConfigure.profile.buildingBlocks
})

export default connect(mapStateToProps)(LivePreview)
